import T from "@/components/forms/TextField.module.css";

import { ariaTanstackFieldProps } from "@/components/forms/ariaTanstackFieldProps.tsx";
import { CurrencyBalance, CurrencySymbol } from "@/components/l10n/Currency.tsx";
import { ModalResult } from "@/components/modal/ModalResult.ts";
import { WModal, WModalContent } from "@/components/modal/WModal.tsx";
import { Separator } from "@/components/Separator.tsx";
import { useConfig } from "@/config.ts";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import { AllFees } from "@/modules/billing/AllFees.tsx";
import { BaseCurrencyField } from "@/modules/billing/BaseCurrencyField.tsx";
import { enableDisableAutomatedTopUpMutation } from "@/modules/billing/billingAccountQuery.ts";
import type { EBillingAccount } from "@/modules/billing/billingLogic.tsx";
import { accountCardMethodsAtom, getPrimaryCard } from "@/modules/billing/BoundMethod.ts";
import { TopUpAmountLabel } from "@/modules/billing/payment/PaymentModal.tsx";
import { ErrorTexts } from "@/modules/billing/payment_forms/components.tsx";
import { useCurrentCurrency } from "@/modules/currency/useCurrentCurrency.ts";
import { useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import { Trans, useLingui } from "@lingui/react/macro";
import { useForm, useStore } from "@tanstack/react-form";
import type { ReactNode } from "react";
import { Input, Label, NumberField } from "react-aria-components";

export interface AutomatedTopUpModalProps {
    billingAccount: EBillingAccount;
}

export function AutomatedTopUpModalContent({ billingAccount }: AutomatedTopUpModalProps) {
    //#region Hooks
    const { t } = useLingui();
    const { minimumTopupAmount } = useConfig();
    const currency = useCurrentCurrency();
    const toggleAutomatedTopUpMutation = useStandardMutation(enableDisableAutomatedTopUpMutation);

    const { id, recurring_payment_threshold, recurring_payment_amount, is_recurring_payment_enabled } =
        billingAccount.account;

    const cardMethods = useSuspenseQueryAtom(accountCardMethodsAtom(id));
    const primaryCardMethod = getPrimaryCard(cardMethods);

    const form = useForm({
        defaultValues: {
            threshold: currency.fromSite(recurring_payment_threshold ?? minimumTopupAmount),
            amount: currency.fromSite(recurring_payment_amount ?? minimumTopupAmount),
        },
        async onSubmit({ value: { threshold, amount } }) {
            await toggleAutomatedTopUpMutation.mutateAsync({
                id,
                body: {
                    recurring_payment_threshold: currency.toSite(threshold),
                    recurring_payment_amount: currency.toSite(amount),
                    is_recurring_payment_enabled: true,
                },
            });
        },
    });
    const isSubmitting = useStore(form.store, (s) => s.isSubmitting);
    //#endregion

    return (
        <WModalContent
            title={t`Automated top up`}
            label={!is_recurring_payment_enabled ? t`Enable` : t`Save`}
            modalAction={async () => {
                await form.handleSubmit();
                return form.state.isValid ? ModalResult.CLOSE : ModalResult.KEEP_OPEN;
            }}
            className="flex flex-col gap-3 max-w-32rem"
        >
            <div className="flex gap-0.5rem w-full">
                <div className="w-50%">
                    <form.Field
                        name="threshold"
                        validators={{
                            // `NaN` when the field is empty
                            onBlur: ({ value }) =>
                                Number.isNaN(value) || value <= 0 ? t`Amount should be positive` : null,
                        }}
                    >
                        {(api) => (
                            <NumberField
                                className={T.NumberField}
                                autoFocus
                                isDisabled={isSubmitting}
                                {...ariaTanstackFieldProps(api)}
                            >
                                <Label className={T.Label}>
                                    <Trans>When balance goes below</Trans> (<CurrencySymbol />)
                                </Label>
                                <Input className={T.Input} />
                                <ErrorTexts errors={api.state.meta.errors} />
                            </NumberField>
                        )}
                    </form.Field>
                </div>

                <div className="w-50%">
                    <form.Subscribe selector={(s) => s.values.threshold}>
                        {(threshold) => <BaseCurrencyField value={threshold} currency={currency} />}
                    </form.Subscribe>
                </div>
            </div>

            <div>
                <Separator />
            </div>

            <div className="w-100%">
                <form.Field
                    name="amount"
                    validators={{
                        onBlur: ({ value }) =>
                            Number.isNaN(value) || value < currency.fromSite(minimumTopupAmount)
                                ? t`Amount should not be less than than minimum top up`
                                : null,
                    }}
                >
                    {(api) => (
                        <NumberField
                            className={T.NumberField}
                            isDisabled={isSubmitting}
                            {...ariaTanstackFieldProps(api)}
                        >
                            <Label className={T.Label}>
                                <TopUpAmountLabel
                                    currency={currency}
                                    minimumAmount={<CurrencyBalance value={minimumTopupAmount} />}
                                />
                            </Label>
                            <div className="w-50%">
                                <Input className={T.Input} />
                            </div>
                            <ErrorTexts errors={api.state.meta.errors} />
                        </NumberField>
                    )}
                </form.Field>
            </div>

            <form.Subscribe selector={(s) => s.values.amount}>
                {(amount) => (
                    <AllFees
                        billingAccount={billingAccount}
                        amount={currency.toSite(amount)}
                        method={primaryCardMethod}
                    />
                )}
            </form.Subscribe>
        </WModalContent>
    );
}

export function AutomatedTopUpModal({ button, ...props }: AutomatedTopUpModalProps & { button: ReactNode }) {
    return (
        <WModal button={button}>
            <AutomatedTopUpModalContent {...props} />
        </WModal>
    );
}
